// src/encounter/lib/dx-primary.ts
// The one-primary invariant for A/P diagnoses (Flutter: "one diagnosis must be Primary").
// Exactly one DxCard should carry isPrimary. Shared by the load mapper (legacy/blank docs with no
// flag) and the remove handler (deleting the primary must promote another) so the rule lives once.
import type { DxCard } from '@scripsteam/scrips-signal-ds';

/**
 * Coerce a diagnosis list to exactly one primary:
 *  - 0 diagnoses → unchanged (empty).
 *  - none flagged → the FIRST becomes primary.
 *  - exactly one → unchanged.
 *  - more than one → first-wins (the rest demoted).
 * Returns the same array reference when it already satisfies the invariant (no needless re-render).
 */
export function ensureOnePrimary(diagnoses: DxCard[]): DxCard[] {
  if (diagnoses.length === 0) return diagnoses;
  const primaryCount = diagnoses.reduce((n, d) => n + (d.isPrimary ? 1 : 0), 0);
  if (primaryCount === 1) return diagnoses;
  const firstPrimaryIdx = diagnoses.findIndex((d) => d.isPrimary);
  const keepIdx = firstPrimaryIdx >= 0 ? firstPrimaryIdx : 0; // none flagged → first; many → first-flagged
  return diagnoses.map((d, i) => {
    const shouldBePrimary = i === keepIdx;
    return d.isPrimary === shouldBePrimary ? d : { ...d, isPrimary: shouldBePrimary };
  });
}
